import type { FilterGroupItem, FilterItem } from '@civilgrid/ui/utils';

/** A bare calendar date — no time, no zone: `2026-07-01`. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The wire format for a date filter: the calendar date the user picked, no time and no zone.
 *
 * The DatePicker returns `new Date(y, m, d).toISOString()` — local midnight as a UTC instant — so
 * west of UTC "7/1" arrives as `2026-07-01T07:00:00.000Z` and compares as 07:00 on the 1st against
 * the naive `lastRefresh` timestamp, excluding rows stored at midnight (PSCO-80). Local
 * getFullYear/getMonth/getDate recover the picked day because the instant encodes local midnight.
 *
 * Non-strings, already-bare dates, and unparseable strings pass through unchanged.
 */
export function toCalendarDate<T>(value: T): T | string {
  if (typeof value !== 'string' || CALENDAR_DATE.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Copy of `filters` with every `filterValueConfig.type === 'date'` line's value as a calendar
 * date. Apply to the outgoing request payload only — filter *state* keeps the ISO instant, which
 * the DatePicker round-trip, the AppliedFilter chip, and sessionStorage read back through
 * `new Date(value)` (a bare date would misparse as UTC midnight and show the previous day west
 * of UTC).
 */
export function toCalendarDateFilters(filters?: FilterGroupItem): FilterGroupItem | undefined {
  if (!filters) return filters;
  return { ...filters, items: filters.items.map(toCalendarDateItem) };
}

function toCalendarDateItem(item: FilterItem): FilterItem {
  if (item.type === 'group') return { ...item, items: item.items.map(toCalendarDateItem) };
  if (item.filterType?.filterValueConfig?.type !== 'date') return item;
  return { ...item, value: toCalendarDate(item.value) };
}
